// Tiny synthesized chime for the "ticket done" reward. Uses Web Audio so there
// is no asset to load; the click that moves the ticket satisfies the browser
// autoplay gesture requirement.

// Master volume (0..1) applied to every note. Editable by admins (stored in
// Firestore config/sound, loaded on startup via setChimeVolume).
export const DEFAULT_CHIME_VOLUME = 0.25;
let masterGain = DEFAULT_CHIME_VOLUME;

export function setChimeVolume(v: number): void {
  masterGain = Math.min(1, Math.max(0, v));
}

export function getChimeVolume(): number {
  return masterGain;
}

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined' || !window.AudioContext) return null;
  if (!ctx) ctx = new AudioContext();
  return ctx;
}

// Schedules one note with a quick attack and exponential decay. peak is the base
// level; the master gain scales it.
function note(
  ac: AudioContext,
  freq: number,
  start: number,
  dur: number,
  peak: number,
  type: OscillatorType,
): void {
  const level = peak * masterGain;
  if (level <= 0) return;
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(level, start + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(gain).connect(ac.destination);
  osc.start(start);
  osc.stop(start + dur);
}

// Festive rising arpeggio (C5-E5-G5-C6) + a high sparkle "chink" at the end.
export function playDoneChime(): void {
  const ac = getCtx();
  if (!ac) return;
  if (ac.state === 'suspended') void ac.resume();

  const now = ac.currentTime;
  const arp = [523.25, 659.25, 783.99, 1046.5];
  arp.forEach((freq, i) => {
    note(ac, freq, now + i * 0.07, 0.3, 0.16, 'triangle');
  });

  // sparkle: two quick bright high notes layered for the "chi-chink"
  const sparkleStart = now + arp.length * 0.07;
  note(ac, 2093.0, sparkleStart, 0.18, 0.1, 'square');
  note(ac, 3135.96, sparkleStart + 0.04, 0.16, 0.07, 'square');
}
